<template>
	<div class="panel-wrapper hidden-print">
		<div class="favorites-section" data-testid="favoritesSection">
			<div class="container">
				<div class="row panel-head">
					<div class="col-xs-12">
						<h2 class="h6 h-static">Favorite Class Schedules</h2>
					</div>
				</div>
				<div class="row" v-if="favorites && favorites.length === 0">
					<div v-if="userKnown" class="col-xs-12 is-empty">You have no favorite schedules. Save a favorite schedule for next time by applying filters and then selecting &ldquo;Add Schedule to Favorites&rdquo; below.</div>
					<div v-else class="col-xs-12 is-empty">Please <a :href="loginUrl">log in</a> to view your Favorites.</div>
				</div>
				<div class="row" v-if="favorites && favorites.length > 0">
					<div class="col-xs-12 col-md-4" data-testid="favorites" v-for="favorite in favorites" :key="favorite.name">
						<div class="favorite-block">
							<button class="btn btn-link btn-link-simple btn-sm btn-favorite" @click="handleLoadFavorite(favorite)">
								<span class="sr-only">Load </span>{{ favorite.name }}<span class="sr-only"> Favorite Schedule</span>
							</button>
							<button class="btn btn-link btn-link-simple btn-sm btn-favorite-icon" data-testid="favoritesEditBtn" @click="handleEditShow(favorite, false)"><span class="ico-edit-sm"></span><span class="sr-only">Edit {{ favorite.name }} Favorite Schedule</span></button>
							<button class="btn btn-link btn-link-simple btn-sm btn-favorite-icon" data-testid="favoritesDeleteBtn" @click="handleDeleteShow(favorite)"><span class="ico-trash"></span><span class="sr-only">Delete {{ favorite.name }} Favorite Schedule</span></button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Edit/Create -->
		<Modal ref="editModal" ariaLabelled="favoriteEditModalTitle">
			<template #title>{{ editModalTitle }}</template>
			<template #default>
				<Forms
					ref="editForm"
					:formDefinition="formDefinition"
					@changed="handleFormChanged"
					@validate="handleValidationTrigger"/>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" @click="handleEditClose()">Cancel</button>
				<button type="button" id="edit-submit" class="btn btn-primary" data-testid="favoritesAddBtn" @click="handleEditSubmit()" :disabled="!isButtonEnabled">{{ editModalCta }}</button>
			</template>
		</Modal>

		<!-- Delete -->
		<Modal ref="deleteModal" ariaLabelled="favoriteDeleteModalTitle">
			<template #title>Delete Schedule from Favorites</template>
			<template #default>
				<p>Are you sure you want to delete <b>{{ selectedSearchName }}</b>?</p>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" data-testid="noBtn" @click="handleDeleteClose()">No</button>
				<button type="button" class="btn btn-primary" data-testid="yesBtn" @click="handleDeleteSubmit()" autofocus>Yes</button>
			</template>
		</Modal>

	</div>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import Modal from '@/components/schreg-common/Modal.vue';
import Forms from '@/components/schreg-common/Forms/Forms.vue';
import { momentProvider } from '@/services/clock.service';
import { NotificationService } from '@/services/notification.service';

export default defineComponent({
	name: 'FavoritesSub',
	components: {
		Modal,
		Forms
	},
	props: {
		userKnown: {
			type: Boolean,
			default: true
		},
		loginUrl: {
			type: String,
			default: ''
		},
		favorites: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			selectedSearch: null,
			selectedSearchName: null,
			isValid: true,
			existsError: false,
			creatingNew: false,
			editModalTitle: '',
			editModalCta: '',
			isButtonEnabled: false,

			formDefinition: [
				{
					fields: [
						{
							type: 'text',
							fieldName: 'scheduleName',
							fieldPrompt: 'Schedule Name',
							required: true,
							maxlength: '50',
							autofocus: true,
							enterTarget: '#edit-submit'
						}
					]
				}
			]
		};
	},
	methods: {
		createNewFavorite(filters) {
			if (this.userKnown) {
				this.handleEditShow({
					name: '',
					lastUsed: momentProvider(),
					filters: filters,
					id: this.generateId(filters)
				}, true);
			} else {
				NotificationService.danger('<p><strong>Please <a href="' + this.loginUrl + '">log in</a> to add a Favorite.</strong></p>').trigger();
			}
		},

		handleEditShow(favorite, creatingNew) {
			this.creatingNew = creatingNew;
			this.editModalTitle = this.creatingNew ? 'Add Schedule To Favorites' : 'Edit Schedule Name';
			this.editModalCta = this.creatingNew ? 'Add' : 'Save';
			this.selectedSearch = favorite;
			this.$refs.editForm.setValue('scheduleName', favorite.name);
			this.$refs.editForm.resetDirty();
			this.$refs.editModal.show();
		},
		handleEditSubmit() {
			const formValues = this.$refs.editForm.values();
			const valid = this.handleValidationTrigger();

			if (valid) {
				this.selectedSearch.name = formValues.scheduleName;
				this.updateSearches(this.selectedSearch.id, this.selectedSearch);
				this.handleEditClose();
				this.selectedSearch = null;
				if (this.creatingNew) {
					NotificationService.success('<p>Schedule successfully added to Favorites.</p>').trigger();
				} else {
					NotificationService.success('<p>Favorite schedule name successfully edited.</p>').trigger();
				}
			}
		},
		handleEditClose() {
			this.$refs.editModal.hide();
		},
		handleFormChanged(field, value) {
			if (field === 'scheduleName') this.isButtonEnabled = !!value;
		},
		handleDeleteShow(favorite) {
			this.creatingNew = false;
			this.selectedSearch = favorite;
			this.selectedSearchName = favorite.name;
			this.$refs.deleteModal.show();
		},
		handleDeleteSubmit() {
			this.updateSearches(this.selectedSearch.id);
			this.handleDeleteClose();
			this.selectedSearch = null;
		},
		handleDeleteClose() {
			this.$refs.deleteModal.hide();
		},
		handleValidationTrigger() {
			return this.$refs.editForm.validate([
				this.customCheckSearchName
			]);
		},
		handleLoadFavorite(favorite) {
			this.$emit('loaded', favorite);
		},

		updateSearches(id, addEditSearch) {
			this.$emit('updated', addEditSearch, id);
		},

		customCheckSearchName(fieldName, fieldValue) {
			if (fieldName === 'scheduleName') {
				const existingSearch = _.find(this.favorites, ['name', fieldValue ]);
				if (!existingSearch) return null;

				if (!existingSearch) {
					this.btnDisable = false;
					return null;
				} else {
					this.btnDisable = true;
					return 'This schedule name already exists. Please save again with a new name.';
				}
			}
		},

		generateId(filters) {
			let _id = '';
			_.forEach(Object.keys(filters), group => {
				if (typeof filters[group] === 'boolean') _id += group + filters[group];
				else _id += group + filters[group].join();
			});
			return btoa(_id);
		}
	}
});
</script>

<style lang="scss" scoped>
.is-empty {
	font-size: 14px;
	padding-top: $spacer;
	padding-bottom: $spacer;
}
.favorite-block {
	border-top: 1px solid $gray-10;
	border-bottom: 1px solid $gray-10;
	display: flex;
	margin-top: -1px;
	.btn {
		height: 2.5rem; // 40px
		&:last-child {
			margin-right: -.5rem; // Visually right-align
		}
	}
}
.btn-favorite {
	flex: 1 1 auto;
	text-align: left;
	@include text-truncate();
}
.btn-favorite-icon {
	flex: 0 0 2rem;
}

// Add padding to favorites container when .panel-head is shown
@include media-breakpoint-up(md) {
	.favorites-section .container {
		padding-top: $spacer;
		padding-bottom: $spacer;

		.is-empty {
			padding-top: 0;
			padding-bottom: 0;
		}
	}
}

</style>
